import { Injectable } from '@angular/core';
import { IndexedDbService } from './indexedDb.service';
import { zip, flatMap, map, take, catchError, toArray, switchMap } from 'rxjs/operators';
import { of, Observable, Subject, forkJoin, concat } from 'rxjs';
import { InspectionService } from './inspection.service';
import { InspectionModelService } from './inspectionModel.service';
import { Resource, Inspection } from '../models';
import { PluginComponent } from '../../plugins/plugin.component';
import { OfflineApiService } from './offlineApi.service';
import { LanguageService } from './language.service';
import { BaseService } from './baseService';
import { AppConstants } from '../../utils/appConstants.class';
import { ApiService } from './api.service';
import { InspectionFactoryService } from './inspectionFactory.service';
import { INITIAL_DATA } from '../../partsview/store/reducer/data.reducer';
import { PartsService } from '../../partsview/services';
import { NgxXml2jsonService } from 'ngx-xml2json';
import { ResourceType } from '../../utils';

@Injectable({
  providedIn: 'root'
})
export class OfflineService extends BaseService {
  private counterSubject = new Subject<any>();
  private syncCounterSubject = new Subject<any>();
  private syncTotal: number;

  constructor(
    private indexedDb: IndexedDbService,
    private offlineApi: OfflineApiService,
    private inspectionService: InspectionService,
    private inspectionModelService: InspectionModelService,
    private language: LanguageService,
    private api: ApiService,
    private factory: InspectionFactoryService,
    private partsService: PartsService,
    private ngxXml2jsonService: NgxXml2jsonService
  ) {
    super();
  }

  public includeInspectionInOffline(inspectionId: string): Observable<any> {
    if (this.isConnection) {
      return this.removeInspectionFromOffline(null).pipe(
        switchMap(() => {
          return this.addToOffline(inspectionId).pipe(
            zip(this.getAndStoreData())
          );
        })
      );
    } else {
      return of(null);
    }
  }

  // Returns the removed inspection
  public removeInspectionFromOffline(inspectionId: string): Observable<Inspection | null> {
    if (this.isConnection) {
      localStorage.removeItem(`${AppConstants.OFFLINE_INSPECTION}_${this.currentUserId}`);
      localStorage.removeItem(`${AppConstants.OFFLINE_INSPECTION_NAME}_${this.currentUserId}`);
      this.indexedDb.clearTable(AppConstants.INDEXEDDB_TABLES.images);
      if (inspectionId) {
        return this.indexedDb.remove(AppConstants.INDEXEDDB_TABLES.inspections, inspectionId).pipe(
          flatMap((res) => {
            return this.inspectionService.getInspection(inspectionId, true, true);
          })
        );
      }
      return of(null);
    } else {
      return of(null);
    }
  }

  public uploadData(inspectionId: string): Observable<any> {
    if (this.isConnection) {
      return this.getOfflineData(inspectionId).pipe(
        flatMap((data) => {
          const inspectionJson = JSON.stringify(data.inspection);
          const files = data.files;
          this.syncTotal = files.length;
          if (!data.inspection.isSent) {
            this.syncTotal++;
          }
          this.syncCounterSubject.next(`0/${this.syncTotal}`);
          return this.uploadInspection(inspectionId, inspectionJson, files.length).pipe(
            flatMap((inspectionRes) => {
              if (!inspectionRes) {
                this.syncCounterSubject.next(`1/${this.syncTotal}`);
                return of(inspectionRes);
              }
              return this.uploadFiles(inspectionId, files).pipe(
                map((filesRes) => {
                  if (!filesRes) {
                    return filesRes;
                  }
                  return true;
                  // return this.api.get(`/synchronization/inspections/${inspectionId}/process`);
                })
              );
            })
          );
        })
      );
    }
  }

  public synchronize(inspectionId: string): Observable<boolean> {
    return this.api.get(`/synchronization/inspections/${inspectionId}/process`);
  }

  public checkSynchronization(inspectionId: string): Observable<boolean> {
    return this.api.get(`/synchronization/inspections/${inspectionId}/issynchronizing`);
  }

  public continueLocal(inspectionId: string): Observable<any> {
    return this.factory.continueLocal(inspectionId);
  }

  public get someInspectionOffline() {
    return !!localStorage.getItem(`${AppConstants.OFFLINE_INSPECTION}_${this.currentUserId}`);
  }

  public get getOfflineInspectionId() {
    return localStorage.getItem(`${AppConstants.OFFLINE_INSPECTION}_${this.currentUserId}`);
  }

  public getCounter(): Observable<any> {
    return this.counterSubject;
  }

  public getSyncCounter(): Observable<any> {
    return this.syncCounterSubject;
  }

  private getAndStoreData(): Observable<any> {
    const currentLanguage = localStorage.getItem(AppConstants.CURRENT_LANGUAGE);
    return this.inspectionModelService.getFullModels().pipe(
      map((data) => {
        this.counterSubject.next(1);
        return data;
      }),
      zip(
        this.language.getTranslations(currentLanguage).pipe(
          map((data) => {
            this.counterSubject.next(1);
            return data;
          })
        )
      )
    );
  }

  private addToOffline(inspectionId: string): Observable<any> {
    // use the InspectionService to get the inspection
    // process all plugins to get resources to be offline from downloads object
    // downlaod all resources
    // store them by resource type
    return this.inspectionService.getInspection(inspectionId, true).pipe(
      flatMap((inspection) => {
        const resources: Resource[] = [];
        inspection.allPlugins.forEach((plugin) => {
          if (PluginComponent.downloads[plugin.pluginType]) {
            const pluginResources = PluginComponent.downloads[plugin.pluginType](plugin);
            pluginResources.forEach((pr) => {
              if (!resources.some((r) => pr.resourceType === ResourceType.FormFile && pr.data.name === r.data.name)) {
                resources.push(pr);
              }
            });
          }
        });
        this.counterSubject.next(1);
        localStorage.setItem(
          `${AppConstants.OFFLINE_INSPECTION_APP_TYPE}_${this.currentUserId}`,
          `${inspection.applicationType}`
        );
        localStorage.setItem(`${AppConstants.OFFLINE_INSPECTION}_${this.currentUserId}`, inspectionId);
        localStorage.setItem(`${AppConstants.OFFLINE_INSPECTION_NAME}_${this.currentUserId}`, inspection.name);
        return this.getPartsBooksData(inspection?.data?.machine?.PartsBook).pipe(
          flatMap(() => {
            if (resources.length > 0) {
              return this.offlineApi.getResources(resources, this.counterSubject);
            }
            return of(true);
          })
        );
      })
    );
  }

  private getPartsBooksData(partsBook: string): Observable<any> {
    if (partsBook) {
      const catalog = partsBook.replace(/\s+/g, '').split(',');
      return concat(...catalog.map((c) => this.getPartsViewData(c))).pipe(toArray());
    }
    return of(true);
  }

  private getPartsViewData(partsBook: any): Observable<any> {
    return this.partsService.loadXMLAndParse(partsBook).pipe(
      map((response: INITIAL_DATA) => {
        return response.doc43.partie_machine
          .filter((d) => Array.isArray(d.page))
          .map((d) => {
            return {
              ...d,
              visible: false
            };
          });
      }),
      switchMap((pages) => {
        const images = [];
        pages?.map((d: any) => {
          d.page?.map((p: any) => {
            const numpage = p['@attributes']['numpage'];
            images.push(numpage);
          });
        });
        if (images.length)
          return concat(...images.map((img) => this.partsService.loadSVG(img, partsBook))).pipe(toArray());
        return of(true);
      })
    );
  }

  private getOfflineData(inspectionId: string): Observable<any> {
    if (this.isConnection) {
      return this.indexedDb.read(AppConstants.INDEXEDDB_TABLES.inspections, inspectionId).pipe(
        flatMap((inspection) => {
          if (inspection) {
            return this.indexedDb.readAll(AppConstants.INDEXEDDB_TABLES.images).pipe(
              map((files) => {
                if (files) {
                  return { files: files.filter((f) => f.isNew && !f.isSent), inspection };
                }
                return { files: [], inspection };
              })
            );
          }
        })
      );
    }
  }

  private uploadInspection(inspectionId: string, data: any, itemsCount: number): Observable<any> {
    if (this.isConnection) {
      const model = { data, synchronizationInfo: { nbrItemsToUpload: itemsCount + 1 } };
      return this.indexedDb.read<Inspection>(AppConstants.INDEXEDDB_TABLES.inspections, inspectionId).pipe(
        flatMap((inspection) => {
          if (inspection.isSent) {
            return of(true);
          }
          return this.api.post(`/synchronization/upload/inspections/${inspectionId}`, model).pipe(
            catchError((error: any) => {
              return of(false);
            }),
            flatMap((res) => {
              if (!res.result) {
                return of(res.result);
              }
              inspection.syncObject = res.Synchronization;
              inspection.isSent = true;
              return this.indexedDb
                .put(AppConstants.INDEXEDDB_TABLES.inspections, JSON.parse(JSON.stringify(inspection)), inspection.id)
                .pipe(map((r) => res.result));
            })
          );
        })
      );
    }
  }

  private uploadFiles(inspectionId: string, files: any[]): Observable<any> {
    if (this.isConnection) {
      if (!files || files.length === 0) {
        return of(true);
      }
      const obs: Observable<boolean>[] = [];
      let i = 2;
      files.forEach((file) => {
        const inputFile = new FormData();
        inputFile.append('files', file.file, file.assetId);
        inputFile.append(`type`, file.file.type);
        obs.push(
          this.indexedDb.read(AppConstants.INDEXEDDB_TABLES.images, file.assetId).pipe(
            flatMap((f) => {
              return this.api.postFormData(`/synchronization/upload/inspections/${inspectionId}/file`, inputFile).pipe(
                catchError((error: any) => {
                  return of(false);
                }),
                flatMap((res) => {
                  if (res) {
                    f.isSent = true;
                    this.syncCounterSubject.next(`${i}/${this.syncTotal}`);
                    i++;
                    return this.indexedDb
                      .put(AppConstants.INDEXEDDB_TABLES.images, f, file.assetId)
                      .pipe(map((r) => res));
                  }
                  return of(res);
                })
              );
            }),
            take(1)
          )
        );
      });
      return forkJoin(obs).pipe(
        map((response) => {
          const res = response.every((r) => r);
          return res;
        })
      );
    }
  }

  // private orderFileRequests(obs: Observable<any>[], files: any[], index: number): Observable<any> {
  //     const count = obs.length;
  //     if (count === index) {
  //         return of(true);
  //     }
  //     const asssetId = files[index].assetId;
  //     return obs[index].pipe(flatMap(res => {
  //         if (!res) {
  //             return of(res);
  //         }
  //         this.indexedDb.read(AppConstants.INDEXEDDB_TABLES.images, asssetId).subscribe(file => {
  //             file.isSent = true;
  //             this.indexedDb.put(AppConstants.INDEXEDDB_TABLES.images, file, asssetId);
  //         });
  //         return this.orderFileRequests(obs, files, ++index);
  //     }));

  // }
}
